import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Plus,
  Trash,
  Pencil,
  X,
  Check,
  Drop,
  Flame,
  ThermometerSimple,
  Wrench,
  Warning,
  Clock,
  ArrowClockwise,
  Gauge,
  Note,
  CaretDown
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const EQUIPMENT_TYPES = [
  { value: 'ln2_tank', label: 'LN2 Tank', icon: Drop, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  { value: 'co2_cylinder', label: 'CO2 Cylinder', icon: Flame, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  { value: 'incubator', label: 'Incubator', icon: ThermometerSimple, color: 'text-rose-400', bg: 'bg-rose-500/10' },
  { value: 'bsc', label: 'Biosafety Cabinet', icon: Gauge, color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
  { value: 'centrifuge', label: 'Centrifuge', icon: ArrowClockwise, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { value: 'other', label: 'Other', icon: Wrench, color: 'text-slate-400', bg: 'bg-slate-500/10' },
];

const STATUS_STYLES = {
  operational: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  maintenance: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  offline: 'bg-red-500/15 text-red-400 border-red-500/30',
};

const getTypeConfig = (type) => EQUIPMENT_TYPES.find(t => t.value === type) || EQUIPMENT_TYPES[5];

// ==================== LEVEL GAUGE ====================
const LevelGauge = ({ current, capacity, unit }) => {
  if (!capacity || capacity <= 0) return null;
  const pct = Math.min(100, Math.max(0, ((current || 0) / capacity) * 100));
  const isLow = pct < 20;
  const barColor = isLow ? 'bg-red-500' : pct < 50 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="mt-3" data-testid="level-gauge">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-400">Level</span>
        <span className={`font-medium ${isLow ? 'text-red-400' : 'text-slate-300'}`}>
          {current ?? '?'} / {capacity} {unit}
          {isLow && <Warning size={12} className="inline ml-1 text-red-400" />}
        </span>
      </div>
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// ==================== ADD/EDIT FORM ====================
const EquipmentForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || {
    name: '', type: 'ln2_tank', location: '', status: 'operational',
    capacity: '', current_level: '', unit: 'L',
    last_serviced: '', next_service_due: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const data = {
        ...form,
        capacity: form.capacity ? parseFloat(form.capacity) : null,
        current_level: form.current_level ? parseFloat(form.current_level) : null,
      };
      await onSave(data);
    } finally { setSaving(false); }
  };

  return (
    <div className="card-flat p-6 space-y-4" data-testid="equipment-form">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-100">{initial ? 'Edit Equipment' : 'Add Equipment'}</h3>
        <button onClick={onCancel} className="text-slate-500 hover:text-slate-300"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-slate-400">Name *</Label>
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="LN2 Tank #1" className="input-field w-full mt-1" data-testid="eq-name-input" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Type</Label>
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="input-field w-full mt-1" data-testid="eq-type-select">
            {EQUIPMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Location</Label>
          <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Room 201" className="input-field w-full mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Status</Label>
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="input-field w-full mt-1" data-testid="eq-status-select">
            <option value="operational">Operational</option>
            <option value="maintenance">Maintenance</option>
            <option value="offline">Offline</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Capacity</Label>
          <Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} placeholder="50" className="input-field w-full mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Current Level</Label>
          <Input type="number" value={form.current_level} onChange={e => setForm({ ...form, current_level: e.target.value })} placeholder="35" className="input-field w-full mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Unit</Label>
          <Input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="L, psi, %" className="input-field w-full mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Next Service Due</Label>
          <Input type="date" value={form.next_service_due || ''} onChange={e => setForm({ ...form, next_service_due: e.target.value })} className="input-field w-full mt-1" />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-400">Notes</Label>
        <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" className="input-field w-full mt-1" />
      </div>
      <div className="flex gap-2 pt-2">
        <Button onClick={handleSave} className="btn-primary" disabled={saving} data-testid="eq-save-btn">
          {saving ? '...' : <><Check size={16} className="mr-1" /> Save</>}
        </Button>
        <Button variant="outline" onClick={onCancel} className="btn-secondary">Cancel</Button>
      </div>
    </div>
  );
};

// ==================== LOG EVENT FORM ====================
const LogEventForm = ({ equipmentId, unit, onLogged, onCancel }) => {
  const [event, setEvent] = useState({ event_type: 'reading', value: '', unit: unit || '', notes: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.logEquipmentEvent(equipmentId, {
        ...event,
        value: event.value ? parseFloat(event.value) : null,
      });
      onLogged();
      toast.success('Event logged');
    } catch { toast.error('Failed to log event'); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-700/40 space-y-3 mt-3" data-testid="log-event-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-400">Event Type</Label>
          <select value={event.event_type} onChange={e => setEvent({ ...event, event_type: e.target.value })} className="input-field w-full mt-1" data-testid="event-type-select">
            <option value="reading">Reading</option>
            <option value="refill">Refill</option>
            <option value="maintenance">Maintenance</option>
            <option value="alert">Alert</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Value</Label>
          <Input type="number" value={event.value} onChange={e => setEvent({ ...event, value: e.target.value })} placeholder="Level/reading" className="input-field w-full mt-1" data-testid="event-value-input" />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-400">Notes</Label>
        <Input value={event.notes} onChange={e => setEvent({ ...event, notes: e.target.value })} placeholder="Details..." className="input-field w-full mt-1" />
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="btn-primary" onClick={submit} disabled={saving} data-testid="log-event-btn">
          {saving ? '...' : 'Log Event'}
        </Button>
        <Button size="sm" variant="outline" className="btn-secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

// ==================== EQUIPMENT CARD ====================
const EquipmentCard = ({ item, onEdit, onDelete, onRefresh }) => {
  const [showLog, setShowLog] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const cfg = getTypeConfig(item.type);
  const Icon = cfg.icon;

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    setDeleting(true);
    try {
      await api.deleteEquipment(item.id);
      onRefresh();
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(false); }
  };

  const recentLogs = (item.logs || []).slice(-5).reverse();

  return (
    <div className="card-flat p-5" data-testid={`equipment-card-${item.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cfg.bg}`}>
            <Icon size={20} className={cfg.color} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-100">{item.name}</h3>
            <p className="text-xs text-slate-500">{cfg.label} {item.location && `- ${item.location}`}</p>
          </div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[item.status] || STATUS_STYLES.operational}`}>
          {item.status}
        </span>
      </div>

      <LevelGauge current={item.current_level} capacity={item.capacity} unit={item.unit} />

      {item.next_service_due && (
        <div className="flex items-center gap-1.5 mt-3 text-xs text-slate-500">
          <Clock size={12} />
          Next service: {new Date(item.next_service_due).toLocaleDateString()}
          {new Date(item.next_service_due) < new Date() && (
            <span className="text-red-400 font-medium ml-1">OVERDUE</span>
          )}
        </div>
      )}

      {item.notes && <p className="text-xs text-slate-500 mt-2">{item.notes}</p>}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-700/30">
        <button className="bench-touch-btn bench-touch-btn--primary text-xs flex-1 justify-center" onClick={() => setShowLog(!showLog)} data-testid={`log-btn-${item.id}`}>
          <Note size={14} /> Log Event
        </button>
        <button className="bench-touch-btn bench-touch-btn--secondary text-xs" onClick={() => onEdit(item)}>
          <Pencil size={14} />
        </button>
        <button className="bench-touch-btn bench-touch-btn--ghost text-xs text-red-400" onClick={handleDelete} disabled={deleting}>
          <Trash size={14} />
        </button>
      </div>

      {showLog && (
        <LogEventForm
          equipmentId={item.id}
          unit={item.unit}
          onLogged={() => { setShowLog(false); onRefresh(); }}
          onCancel={() => setShowLog(false)}
        />
      )}

      {/* Recent logs */}
      {recentLogs.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300">
            <CaretDown size={12} className={`transition-transform ${showHistory ? 'rotate-180' : ''}`} />
            Recent logs ({item.logs?.length || 0})
          </button>
          {showHistory && (
            <div className="space-y-1 mt-2">
              {recentLogs.map(log => (
                <div key={log.id} className="text-xs p-2 rounded bg-slate-800/40 border border-slate-700/20 flex items-center justify-between">
                  <div>
                    <span className={`font-medium ${
                      log.event_type === 'alert' ? 'text-red-400' :
                      log.event_type === 'refill' ? 'text-cyan-400' :
                      log.event_type === 'maintenance' ? 'text-amber-400' : 'text-slate-300'
                    }`}>{log.event_type}</span>
                    {log.value != null && <span className="text-slate-400 ml-2">{log.value} {log.unit}</span>}
                    {log.notes && <span className="text-slate-500 ml-2">- {log.notes}</span>}
                  </div>
                  <span className="text-slate-600 whitespace-nowrap ml-2">{log.by} {new Date(log.at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN PAGE ====================
const EquipmentPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getEquipment(filter !== 'all' ? filter : undefined);
      setItems(data || []);
    } catch { toast.error('Failed to load equipment'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editItem) {
        await api.updateEquipment(editItem.id, data);
        toast.success('Equipment updated');
      } else {
        await api.createEquipment(data);
        toast.success('Equipment added');
      }
      setShowForm(false);
      setEditItem(null);
      load();
    } catch { toast.error('Failed to save'); }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const lowLevelItems = items.filter(i => i.capacity && i.current_level != null && (i.current_level / i.capacity) < 0.2);

  return (
    <div data-testid="equipment-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Equipment & Resources</h1>
          <p className="text-slate-500 mt-1">Track LN2 tanks, CO2 cylinders, incubators, and lab equipment</p>
        </div>
        <Button className="btn-primary" onClick={() => { setEditItem(null); setShowForm(true); }} data-testid="add-equipment-btn">
          <Plus size={16} className="mr-2" /> Add Equipment
        </Button>
      </div>

      {/* Low level alerts */}
      {lowLevelItems.length > 0 && (
        <div className="p-4 rounded-xl bg-red-500/8 border border-red-500/20 mb-6" data-testid="low-level-alerts">
          <div className="flex items-center gap-2 mb-2">
            <Warning size={18} className="text-red-400" />
            <span className="text-sm font-semibold text-red-400">Low Level Alerts</span>
          </div>
          <div className="space-y-1">
            {lowLevelItems.map(i => (
              <p key={i.id} className="text-sm text-red-300">
                {i.name}: {i.current_level} / {i.capacity} {i.unit} ({Math.round((i.current_level / i.capacity) * 100)}%)
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Type filter */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === 'all' ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30' : 'bg-slate-800/40 text-slate-400 border border-slate-700/30 hover:border-slate-600/50'}`} data-testid="filter-all">
          All
        </button>
        {EQUIPMENT_TYPES.map(t => (
          <button key={t.value} onClick={() => setFilter(t.value)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === t.value ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30' : 'bg-slate-800/40 text-slate-400 border border-slate-700/30 hover:border-slate-600/50'}`} data-testid={`filter-${t.value}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="mb-6">
          <EquipmentForm
            initial={editItem}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditItem(null); }}
          />
        </div>
      )}

      {/* Equipment Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <Wrench size={48} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">No equipment found</p>
          <p className="text-sm text-slate-500 mt-1">Add your first piece of lab equipment to start tracking</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map(item => (
            <EquipmentCard key={item.id} item={item} onEdit={handleEdit} onDelete={load} onRefresh={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default EquipmentPage;
